/**
 * The portal extension of the TurboTwin icon vocabulary: concepts only the
 * admin portal has, in the same shape as `Icons` from `turbotwin-sdk/icons`.
 * A concept that exists upstream is used from `Icons`, never repeated here
 * (portalIcons.test.ts checks that). Twin content, views, common actions and
 * statuses that are missing upstream are asked for in turbotwin-sdk.
 *
 * Use `Icons.view.dashboard` for Leadership and `Icons.view.access` for
 * Members; they have no entry here.
 *
 * Entries are Material icon names; their path data is generated into
 * portalIconPaths.generated.ts by `node scripts/generate-portal-icon-paths.mjs`
 * (run in admin-portal), which parses PORTAL_ICON_VOCABULARY below: keep one
 * `concept: 'MaterialName',` entry per line.
 */
import type { IconSpec } from 'turbotwin-sdk/icons';
import { PORTAL_MATERIAL_PATHS } from './portalIconPaths.generated';

export const PORTAL_ICON_VOCABULARY = {
  /** Portal pages and nav entries. */
  view: {
    orientations: 'Explore',
    orientationEditor: 'EditLocationAlt',
    myOrientations: 'School',
    orientationAssignment: 'GridView',
    cohorts: 'Groups',
    reviewQueue: 'FactCheck',
    liveSessions: 'Podcasts',
    workforce: 'People',
  },
  action: {
    /** Capture the current viewer camera into a stop or beat. AddAPhoto: PhotoCamera has a <circle>, not a path. */
    captureView: 'AddAPhoto',
    dockPanel: 'ViewSidebar',
    dockBottom: 'HorizontalSplit',
    overlayPanel: 'PictureInPicture',
    themeLight: 'LightMode',
    themeDark: 'DarkMode',
    keyboardShortcuts: 'Keyboard',
    moveUp: 'ArrowUpward',
    moveDown: 'ArrowDownward',
    expand: 'ExpandMore',
    collapse: 'ExpandLess',
    chevronRight: 'ChevronRight',
    chevronLeft: 'ChevronLeft',
    /** The notification inbox. Not `Icons.view.alerts`: those are twin alert rules. */
    notifications: 'Notifications',
    layers: 'Layers',
  },
  status: {
    /** Shared, used in N orientations. Not RestartAlt: that is `Icons.action.resetView`. */
    shared: 'Share',
    /** The live dot. Not Sensors: that is `Icons.content.telemetry`. */
    live: 'RadioButtonChecked',
    /** A quiz option the trainee did not pick (review queue answer chips). */
    answerUnpicked: 'RadioButtonUnchecked',
    /** A quiz option the trainee picked, answer key hidden. Same glyph as live, different concept. */
    answerPicked: 'RadioButtonChecked',
  },
} as const;

type Vocab = typeof PORTAL_ICON_VOCABULARY;

export const PortalIcons = Object.fromEntries(
  Object.entries(PORTAL_ICON_VOCABULARY).map(([group, concepts]) => [
    group,
    Object.fromEntries(
      Object.entries(concepts).map(([name, material]) => {
        const path = PORTAL_MATERIAL_PATHS[material];
        if (!path) throw new Error(`portal icon ${name}: no path for ${material}, run node scripts/generate-portal-icon-paths.mjs`);
        return [name, { name, material, path }];
      }),
    ),
  ]),
) as { readonly [G in keyof Vocab]: { readonly [C in keyof Vocab[G]]: IconSpec } };
